import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import urlJoin from 'url-join';
import { addMinutes } from 'date-fns';
import { normalizeMediaObject, getImageByWidth } from 'api';
import { useAnalytics } from 'hooks';
import { Route, Switch } from 'react-router-dom';
import ResizeDetector from 'react-resize-detector';
import { Timeline, FlexPlayer } from 'msp-components';
import ExternalMediaPlayer from 'components/ExternalMediaPlayer';
import Error404 from 'components/Error404';

const normalizeSlideData = (slides, imgWidth = 1500) => slides.map(slide => {
  const origDate = new Date(slide.date);
  const date = addMinutes(origDate, origDate.getTimezoneOffset());

  let imageUrl;
  let mediaSourceType;
  let mediaSource;

  if (slide.media) {
    if (slide.media.type === 'image') {
      imageUrl = (getImageByWidth(slide.media.versions, imgWidth) || {}).url;
    } else {
      const { sourceType, source } = normalizeMediaObject(slide.media);
      mediaSourceType = sourceType;
      mediaSource = source;
    }
  }

  let renderMedia;
  if (slide.mediaSource === 'url' && !!slide.mediaUrl) {
    renderMedia = () => (
      <FlexPlayer
        caption={slide.mediaDescription}
        credit={slide.mediaCredit}
        playerContent={<ExternalMediaPlayer mediaUrl={slide.mediaUrl} />}
      />
    );
  }

  return {
    date,
    dateText: slide.dateFormatted,
    title: slide.headline,
    description: slide.description,
    imageUrl,
    mediaSourceType,
    mediaSource,
    renderMedia,
    mediaDescription: slide.mediaDescription,
    mediaCredit: slide.mediaCredit,
    layout: slide.layout || null,
  };
});

const TimelineTitle = ({ routeProps, canonicalPath, slides }) => {
  const [containerWidth, setContainerWidth] = useState();
  const handleResize = width => setContainerWidth(width);

  const { match, history } = routeProps;
  const titleBasePath = canonicalPath || match.url;

  const timelineItems = useMemo(() => normalizeSlideData(slides, containerWidth && containerWidth * 1.5), [slides, containerWidth]);
  const handleItemSelect = index => history.push(urlJoin(titleBasePath, (index + 1).toString()));

  const { trackMediaPlay, trackMediaProgress } = useAnalytics();

  return (
    <Switch>
      <Route
        exact
        path={urlJoin(match.url, ':slideIndex?')}
        render={props => {
          // Validate slide index
          // eslint-disable-next-line
          const { slideIndex } = props.match.params;
          const index = parseInt(slideIndex || 1, 10);
          if (isNaN(index) || index < 1 || index > slides.length) return <Error404 />;

          const activeItemIndex = index - 1;
          return (
            <>
              <ResizeDetector
                handleWidth
                onResize={handleResize}
                refreshMode="debounce"
                refreshRate={500}
              />
              <Timeline
                items={timelineItems}
                activeItemIndex={activeItemIndex}
                onSelectItem={handleItemSelect}
                onMediaPlay={trackMediaPlay}
                onMediaProgress={trackMediaProgress}
              />
            </>
          );
        }}
      />
      <Route component={Error404} />
    </Switch>
  );
};

TimelineTitle.propTypes = {
  routeProps: PropTypes.object,
  canonicalPath: PropTypes.string,
  slides: PropTypes.arrayOf(PropTypes.object),
};

export default TimelineTitle;
